import { supabase } from '../supabase'
import { enqueue, failedRowIds, pendingRowIds } from '../sync/outbox'
import { markSynced } from '../../utils/localCache'
import { kick } from '../sync/drainer'
import { syncBus } from '../sync/bus'
import { applyLocal, readRows, replaceRows } from '../local/cache'
import { orderToRow, rowToOrder, settlementToRow } from './rows'
import type { NewOrder, Order, PaymentMethod } from '../../types'
import { isViewMode, VIEW_MODE_EVENT } from '../viewMode'

/**
 * Orders, written through the outbox and read from the local cache.
 *
 * The signatures are unchanged from the Firestore version, and that is the
 * point: SalePage calls `createOrder` and gets an id back on the same tick,
 * exactly as before, so nothing above this layer had to be touched.
 *
 * Keeping that promise is what makes the till usable without a signal. A write
 * is applied to the local cache and queued; the network is somebody else's
 * problem, handled by the drainer. Nothing here awaits the server, because a
 * cashier at a counter cannot wait for it.
 */

const TABLE = 'orders' as const

/** Queues a write and shows it immediately. Never awaited by callers. */
function write(
  tenantId: string,
  id: string,
  type: 'insert' | 'update',
  payload: Record<string, unknown>,
  dayKey?: string,
): void {
  // Görüntüleme modu: kuyruğa da yazılmıyor, ekranda kaydedilmiş gibi görünmesin.
  if (isViewMode()) {
    window.dispatchEvent(new Event(VIEW_MODE_EVENT))
    return
  }
  void enqueue({ tenantId, table: TABLE, rowId: id, type, payload })
    .then((seq) => applyLocal(TABLE, tenantId, id, payload, seq, dayKey))
    .then(() => kick())
    .catch((err) => console.error('[orders] yerel yazma başarısız', id, err))
}

/**
 * Writes an order and returns its id immediately, deliberately synchronous.
 *
 * The id is generated here rather than by the server, because the receipt has
 * to be printed before anything has been acknowledged. SalePage takes this id
 * and goes straight to the printer.
 */
export function createOrder(tenantId: string, data: NewOrder): string {
  const id = crypto.randomUUID()
  const row = orderToRow(tenantId, {
    ...data,
    id,
    createdAtLocal: Date.now(),
  })
  write(tenantId, id, 'insert', row, data.dayKey)
  return id
}

/**
 * Opens a waiter's ticket. No receipt number and no payment yet, both are
 * decided at the till, so an abandoned ticket never burns a receipt number.
 */
export function openTicket(
  tenantId: string,
  data: Omit<NewOrder, 'receiptNo' | 'paymentMethod'>,
  /**
   * Told when the server refuses the write for good.
   *
   * Not called for a lost connection: being offline is not a rejection, and
   * telling a waiter their ticket failed because the wifi dropped would be
   * both wrong and alarming. Only a permanent refusal, a removed account, a
   * state the server will not accept, reaches this.
   */
  onRejected?: (err: unknown) => void,
): string {
  const id = crypto.randomUUID()
  const row = orderToRow(tenantId, {
    ...data,
    id,
    receiptNo: '',
    status: 'open',
    createdAtLocal: Date.now(),
  })

  if (onRejected) {
    const off = syncBus.on('permanent-failure', (e) => {
      if (e.rowId !== id) return
      off()
      onRejected(new Error(e.failure.message))
    })
  }

  write(tenantId, id, 'insert', row, data.dayKey)
  return id
}

/** Replaces the contents of an open ticket, a waiter adding a round. */
export function updateTicket(
  tenantId: string,
  orderId: string,
  lines: Order['lines'],
  itemCount: number,
  totalKurus: number,
  /**
   * True when this round must not produce paper anywhere.
   *
   * The till that owns the printer is usually a different machine from the one
   * the round was rung in on, and it cannot tell a deliberate silent save from
   * a waiter's order, it sees the same change either way.
   */
  silent = false,
): void {
  write(tenantId, orderId, 'update', {
    lines,
    item_count: itemCount,
    total_kurus: totalKurus,
    silent,
    updated_at_local: Date.now(),
  })
}

/**
 * Moves an open ticket to a different table. Customers change seats and
 * waiters mistype the table, and without this the only fix was voiding the
 * ticket and re-entering every line.
 */
/** Açık adisyona alınan ara ödemelerin yeni toplamı (22 Eki 2026). */
export function setTicketPrepaid(tenantId: string, orderId: string, prepaidKurus: number): void {
  write(tenantId, orderId, 'update', { prepaid_kurus: prepaidKurus, updated_at_local: Date.now() })
}

/** Masa notu (4 Kas 2026). Boş metin notu siler. */
export function setTicketNote(tenantId: string, orderId: string, note: string): void {
  write(tenantId, orderId, 'update', { table_note: note.trim() || null, updated_at_local: Date.now() })
}

export function moveTicket(tenantId: string, orderId: string, tableName: string): void {
  write(tenantId, orderId, 'update', { table_name: tableName })
}

/**
 * Settles an open ticket: this is where the receipt number is issued and the
 * payment recorded, in one write.
 */
export function settleTicket(
  tenantId: string,
  orderId: string,
  settlement: Partial<Order> & { receiptNo: string; paymentMethod: PaymentMethod },
): void {
  write(tenantId, orderId, 'update', settlementToRow(settlement))
}

/**
 * @param mergedInto  Birleştirmede hedef adisyon: mutfak tetiği fişleri iptal
 *   etmek yerine oraya aktarıyor (23 Eki 2026).
 */
export function cancelTicket(
  tenantId: string,
  orderId: string,
  voidedBy: string,
  mergedInto?: string,
): void {
  write(tenantId, orderId, 'update', {
    status: 'void',
    voided_at: new Date().toISOString(),
    voided_by: voidedBy,
    ...(mergedInto ? { merged_into: mergedInto } : {}),
  })
}

/**
 * Merges one open ticket into another and voids the source, two tables
 * pushed together pay as one.
 *
 * The merge is queued before the void, and the queue preserves that order, so
 * a failure between the two leaves the items duplicated rather than lost: a
 * visible double is fixable at the till, a vanished round is not.
 */
export function mergeTickets(
  tenantId: string,
  source: Order,
  target: Order,
  lines: Order['lines'],
  itemCount: number,
  totalKurus: number,
  voidedBy: string,
): void {
  // Sessiz: birleşen ürünler mutfağa ikinci kez gitmesin (mutfak ekranı
  // tetiği yeni adetleri fiş yapıyor; birleştirme yeni sipariş değil).
  updateTicket(tenantId, target.id, lines, itemCount, totalKurus, true)
  // İki masanın ara ödemeleri de birleşiyor.
  const prepaid = (source.prepaidKurus ?? 0) + (target.prepaidKurus ?? 0)
  if (prepaid > 0) setTicketPrepaid(tenantId, target.id, prepaid)
  cancelTicket(tenantId, source.id, voidedBy, target.id)
}

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

/**
 * Live view of one business day.
 *
 * Cache first, so an offline till shows the day's takings the moment it opens
 * rather than an empty screen. The network then fills in whatever it has.
 */
export function subscribeDayOrders(
  tenantId: string,
  dayKey: string,
  onData: (orders: Order[]) => void,
  onError: (error: Error) => void,
): () => void {
  let cancelled = false

  const emit = async () => {
    const [rows, pending, failed] = await Promise.all([
      readRows(TABLE, tenantId, dayKey),
      pendingRowIds(TABLE),
      failedRowIds(TABLE),
    ])
    if (cancelled) return
    onData(
      rows
        .map((r) => {
          const o = rowToOrder(r)
          return { ...o, pending: pending.has(o.id) || failed.has(o.id) }
        })
        .sort((a, b) => b.createdAtLocal - a.createdAtLocal),
    )
  }

  const pull = async () => {
    if (!supabase) return
    const { data, error } = await supabase
      .from(TABLE)
      .select('*')
      .eq('tenant_id', tenantId)
      .eq('day_key', dayKey)

    if (cancelled) return
    if (error) {
      // Reported but not fatal: the cache is still on screen, which offline is
      // the whole point.
      onError(new Error(error.message))
      return
    }
    await replaceRows(
      TABLE,
      tenantId,
      (data ?? []).map((d) => ({ id: String(d.id), data: d, dayKey })),
      dayKey,
    )
    markSynced()
  }

  void emit()
  void pull()

  const offCache = syncBus.on('cache-changed', (e) => {
    if (e.table === TABLE) void emit()
  })
  // The pending flag lives in the queue, not in the row, so the badge has to
  // redraw when the queue moves.
  const offOutbox = syncBus.on('outbox-changed', () => void emit())

  const channel = supabase
    ?.channel(`orders:${tenantId}:${dayKey}:${crypto.randomUUID()}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: TABLE,
        filter: `tenant_id=eq.${tenantId}`,
      },
      () => void pull(),
    )
    .subscribe((status) => {
      // A reconnect re-reads everything: there is no cursor to resume from, so
      // a full pull is the only way to recover what was missed while down.
      if (status === 'SUBSCRIBED') void pull()
    })

  // Canlı bağlantıya tek başına güvenilmiyor (21 Eki 2026): telefon kilitlenip
  // açılınca bağlantı "bağlı" görünüp sessizce ölebiliyor, kasanın taşıdığı ya
  // da kapattığı adisyon garsonda açık kalıyordu. Uygulama öne gelince ve
  // 30 saniyede bir yeniden çekiliyor; günün siparişleri küçük bir sorgu.
  const onVisible = () => {
    if (!document.hidden) void pull()
  }
  document.addEventListener('visibilitychange', onVisible)
  const timer = window.setInterval(() => {
    if (!document.hidden && navigator.onLine) void pull()
  }, 30_000)

  return () => {
    cancelled = true
    offCache()
    offOutbox()
    document.removeEventListener('visibilitychange', onVisible)
    window.clearInterval(timer)
    if (channel) void supabase?.removeChannel(channel)
  }
}

/** Live list of tickets still awaiting payment. Small by nature. */
export function subscribeOpenTickets(
  tenantId: string,
  dayKey: string,
  onData: (orders: Order[]) => void,
  onError: (error: Error) => void,
): () => void {
  // Filtered in memory rather than at the server. A ticket that gets settled
  // leaves the `status = 'open'` set, and a filtered realtime subscription is
  // not told about rows that leave it, the ticket would sit in the open list
  // for ever.
  return subscribeDayOrders(
    tenantId,
    dayKey,
    (all) =>
      onData(
        all
          .filter((o) => o.status === 'open')
          .sort((a, b) => a.createdAtLocal - b.createdAtLocal),
      ),
    onError,
  )
}

/**
 * Watches only how many of today's orders are still unsent.
 *
 * Entirely local: the queue knows, and asking it costs nothing.
 */
export function subscribePendingCount(
  tenantId: string,
  _dayKey: string,
  onCount: (count: number) => void,
  onError: (error: Error) => void,
): () => void {
  const read = () => {
    // Yalnız bu kurumun, sunucunun reddetmediği kayıtları (takılanlar ayrı).
    void pendingRowIds(TABLE, tenantId)
      .then((ids) => onCount(ids.size))
      .catch((err) => onError(err as Error))
  }
  read()
  return syncBus.on('outbox-changed', read)
}

/**
 * Which days of a month have any sales, for marking them on the report's date
 * picker. One-shot: the picker is opened, read and closed.
 */
export async function daysWithSales(tenantId: string, month: string): Promise<Set<string>> {
  if (!supabase) return new Set()
  const { data, error } = await supabase
    .from(TABLE)
    .select('day_key')
    .eq('tenant_id', tenantId)
    .gte('day_key', `${month}-01`)
    .lte('day_key', `${month}-31`)

  if (error) throw new Error(error.message)
  return new Set((data ?? []).map((d) => String(d.day_key)))
}

/**
 * Reads today's receipt numbers, to recover the local counter after
 * localStorage is cleared mid-service. Without it a till would restart at
 * K1-0001 and issue numbers that are already on printed receipts.
 *
 * Falls back to the cache when there is no signal, the counter has to be
 * recoverable offline too, which is exactly when a device is most likely to
 * have been restarted.
 */
export async function fetchTodaysReceiptNos(
  tenantId: string,
  dayKey: string,
): Promise<string[]> {
  const local = (await readRows(TABLE, tenantId, dayKey))
    .map((r) => String(r.receipt_no ?? ''))
    .filter(Boolean)

  if (!supabase) return local

  const { data, error } = await supabase
    .from(TABLE)
    .select('receipt_no')
    .eq('tenant_id', tenantId)
    .eq('day_key', dayKey)

  if (error) return local
  return [...new Set([...local, ...(data ?? []).map((d) => String(d.receipt_no ?? ''))])].filter(
    Boolean,
  )
}

/**
 * Every completed order across every day, read once on demand.
 *
 * Deliberately not a subscription: this is the only query that reads the whole
 * history, and the analysis screen asks for it when opened.
 */
export async function fetchCompletedOrders(tenantId: string): Promise<Order[]> {
  if (!supabase) return []
  const { data, error } = await supabase
    .from(TABLE)
    .select('*')
    .eq('tenant_id', tenantId)
    .eq('status', 'completed')

  if (error) throw new Error(error.message)
  return (data ?? []).map(rowToOrder).sort((a, b) => a.createdAtLocal - b.createdAtLocal)
}

/**
 * Bir gün aralığının siparişleri (rapor sayfasının dönem görünümü, 11 Eki
 * 2026). Abonelik değil, bir kez okunuyor. Supabase bir seferde 1000 satır
 * veriyor; aralık uzunsa sayfa sayfa okunuyor.
 */
export async function fetchOrdersRange(tenantId: string, from: string, to: string): Promise<Order[]> {
  if (!supabase) return []
  const PAGE = 1000
  const out: Order[] = []
  for (let start = 0; ; start += PAGE) {
    const { data, error } = await supabase
      .from(TABLE)
      .select('*')
      .eq('tenant_id', tenantId)
      .gte('day_key', from)
      .lte('day_key', to)
      .order('created_at_local', { ascending: true })
      .range(start, start + PAGE - 1)
    if (error) throw new Error(error.message)
    out.push(...(data ?? []).map(rowToOrder))
    if (!data || data.length < PAGE) break
  }
  return out
}

/** The one write that is awaited: the report screen wants to know it landed. */
export async function voidOrder(
  tenantId: string,
  orderId: string,
  voidedBy: string,
): Promise<void> {
  if (!supabase) throw new Error('Supabase yapılandırılmamış')
  const { error } = await supabase
    .from(TABLE)
    .update({
      status: 'void',
      voided_at: new Date().toISOString(),
      voided_by: voidedBy,
    })
    .eq('id', orderId)
    .eq('tenant_id', tenantId)

  if (error) throw new Error(error.message)
}

/**
 * Tek sipariş (3 Kas 2026): Cari Hesap'ta borcun "ne yemiştim" bilgisi.
 * Bulunamazsa (silinmiş ya da başka kurumun) null.
 */
export async function fetchOrderById(tenantId: string, orderId: string): Promise<Order | null> {
  if (!supabase) return null
  const { data, error } = await supabase
    .from(TABLE)
    .select('*')
    .eq('tenant_id', tenantId)
    .eq('id', orderId)
    .maybeSingle()
  if (error) throw new Error(error.message)
  return data ? rowToOrder(data) : null
}
